import React, { useContext, useState } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { newSeed } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two seed controls (ADR 0009 §3), as two components since ADR 0061.
 *
 * - "New problems" draws a fresh seed and changes nothing else - the capability
 *   the site had before seeding, kept. It sits in the bar above the preview.
 * - "Rebuild a printed sheet" takes the six-character code from the bottom of a
 *   printed sheet and sets the seed to it. It sits in the strip under the
 *   preview, beside the line that says which code this sheet prints.
 *
 * ADR 0009 §3 put both next to the preview because they are for someone holding
 * a page, not someone choosing options. The ADR 0023 amendment moved them into
 * the advanced tier when the single-column page had nowhere else for them; the
 * workbench has a place next to the sheet again, so they go back to it.
 * Neither can open the advanced panel: they are actions, with no default to
 * differ from.
 *
 * Providers other than MathFactsGenerator (flash cards, quiz, a test harness)
 * do not supply a seed, and both render nothing there.
 */
export function NewProblemsButton() {
    const { seed, setSeed } = useContext(SettingsContext);
    const t = useTranslation();

    if (!seed || !setSeed) {
        return null;
    }

    return (
        <button
            type="button"
            className="btn btn-secondary newProblems"
            onClick={() => setSeed(newSeed())}
        >
            {t('settings.newProblems')}
        </button>
    );
}

export function RebuildForm() {
    const { seed, setSeed } = useContext(SettingsContext);
    const [code, setCode] = useState("");
    const t = useTranslation();

    if (!seed || !setSeed) {
        return null;
    }

    function rebuild(event: React.FormEvent) {
        event.preventDefault();
        const trimmed = code.trim().toUpperCase();
        if (trimmed) {
            setSeed!(trimmed);
            setCode("");
        }
    }

    return (
        <form className="rebuildWorksheet" onSubmit={rebuild}>
            <label htmlFor="rebuildCode" title={t('settings.rebuildHint')}>
                {t('workbench.rebuildPrinted')}
            </label>
            <input
                id="rebuildCode"
                className="input rebuildInput"
                value={code}
                maxLength={6}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setCode(event.target.value)}
            />
            <button type="submit" className="btn btn-secondary rebuildSubmit">
                {t('settings.rebuildButton')}
            </button>
        </form>
    );
}
